import { useMemo, useState } from "react";
import {
  addTruthStatement,
  updateTruthStatement,
  type TruthOverview,
} from "../application/round03-service";
import type { CEOOSDatabase } from "../db/database";
import type {
  EvidenceStatement,
  EvidenceStatementType,
  TruthStatementStatus,
} from "../domain/types";
import { safeErrorMessage } from "../privacy/policy";
import type { GovernanceArea } from "../ui/GovernanceTabs";
import { GovernanceTabs } from "../ui/GovernanceTabs";
import { BackIcon } from "../ui/icons";

const TYPE_LABELS: Record<EvidenceStatementType, string> = {
  fact: "事实",
  inference: "推断",
  hypothesis: "假设",
  recommendation: "建议",
};

const STATUS_LABELS: Record<TruthStatementStatus, string> = {
  draft: "草稿",
  active: "有效",
  verified: "已验证",
  falsified: "已证伪",
  superseded: "已被取代",
  archived: "已归档",
};

function sourceLabel(statement: EvidenceStatement): string {
  if (statement.sourceRecordIds.length === 0) return "用户手动添加";
  return `可追溯来源 ${statement.sourceRecordIds.length} 项`;
}

export function TruthAudit({
  database,
  dateKey,
  online,
  overview,
  area,
  onAreaChange,
  onRefresh,
}: {
  database: CEOOSDatabase;
  dateKey: string;
  online: boolean;
  overview: TruthOverview;
  area: GovernanceArea;
  onAreaChange: (area: GovernanceArea) => void;
  onRefresh: () => Promise<void>;
}) {
  const [selectedId, setSelectedId] = useState<string>();
  const [showAdd, setShowAdd] = useState(false);
  const [newType, setNewType] = useState<EvidenceStatementType>("hypothesis");
  const [newContent, setNewContent] = useState("");
  const [notice, setNotice] = useState("");
  const selected = overview.statements.find((statement) => statement.id === selectedId);

  if (selected) {
    return (
      <StatementDetail
        key={`${selected.id}:${selected.updatedAt}`}
        database={database}
        statement={selected}
        allStatements={overview.statements}
        onBack={() => setSelectedId(undefined)}
        onSaved={async () => {
          await onRefresh();
          setNotice("陈述已保存；审计提示已重新计算。");
        }}
      />
    );
  }

  const important = overview.topRisk;
  const importantIssue = important
    ? overview.issues.find((item) => item.statementIds.includes(important.id))
    : undefined;
  const pendingConfirmation = overview.statements.filter(
    (statement) => statement.userConfirmation === "unconfirmed",
  ).length;
  const missingEvidence = overview.issues.filter(
    (item) =>
      item.code === "CEO-TRUTH-001" ||
      item.code === "CEO-TRUTH-002" ||
      item.code === "CEO-TRUTH-005",
  ).length;
  const dueReview = overview.issues.filter((item) => item.code === "CEO-TRUTH-007").length;
  const conflicts = overview.issues.filter((item) => item.code === "CEO-TRUTH-006").length;
  const falsified = overview.statements.filter(
    (statement) => statement.status === "falsified" || statement.status === "superseded",
  );

  const createStatement = async () => {
    if (newContent.trim().length < 2) {
      setNotice("请先写下需要审计的陈述。");
      return;
    }
    try {
      const created = await addTruthStatement(
        database,
        {
          type: newType,
          content: newContent,
          effectiveDate: dateKey,
        },
        new Date().toISOString(),
      );
      setNewContent("");
      setShowAdd(false);
      await onRefresh();
      setSelectedId(created.id);
    } catch (error) {
      setNotice(safeErrorMessage(error));
    }
  };

  return (
    <main className="governance-page truth-page">
      <header className="governance-header">
        <div className="header-row">
          <span className="brand">CEO OS</span>
          <span className="connection-state">{online ? "本地可用" : "离线可用"}</span>
        </div>
        <h1>事实审计</h1>
        <p>系统检查证据与关系，不替你裁定真相。</p>
      </header>

      <GovernanceTabs area={area} onChange={onAreaChange} />

      {notice && (
        <div className="inline-notice" role="status">
          {notice}
        </div>
      )}

      <section className="governance-primary" aria-labelledby="truth-priority">
        <h2 id="truth-priority">当前最需要验证</h2>
        {important ? (
          <>
            <button
              type="button"
              className="open-primary-record"
              onClick={() => setSelectedId(important.id)}
            >
              <strong>{important.content}</strong>
              <span>
                {sourceLabel(important)} · {TYPE_LABELS[important.statementType]}
              </span>
            </button>
            {important.statementType === "hypothesis" && (
              <dl className="evidence-lines">
                <div>
                  <dt>验证条件</dt>
                  <dd>{important.verificationCondition || "尚未填写"}</dd>
                </div>
                <div>
                  <dt>反证条件</dt>
                  <dd>{important.falsificationCondition || "尚未填写"}</dd>
                </div>
              </dl>
            )}
            {importantIssue && (
              <button
                type="button"
                className={`risk-action risk-action--${importantIssue.severity}`}
                onClick={() => setSelectedId(important.id)}
              >
                <span>{importantIssue.message}</span>
                <strong>补充证据</strong>
              </button>
            )}
          </>
        ) : (
          <div className="empty-state">
            <strong>当前没有待审计陈述</strong>
            <p>只有你明确保存的内容才会进入 Truth Inbox。</p>
          </div>
        )}
      </section>

      <section className="governance-list-section" aria-labelledby="truth-pending">
        <h2 id="truth-pending">待处理</h2>
        <div className="governance-count-list">
          <div>
            <span>待确认陈述</span>
            <strong>{pendingConfirmation}</strong>
          </div>
          <div>
            <span>缺少证据的事实</span>
            <strong>{missingEvidence}</strong>
          </div>
          <div>
            <span>可能矛盾的陈述</span>
            <strong>{conflicts}</strong>
          </div>
          <div>
            <span>到期复核</span>
            <strong>{dueReview}</strong>
          </div>
        </div>
      </section>

      {overview.inbox.length > 0 && (
        <section className="governance-list-section" aria-labelledby="truth-inbox">
          <h2 id="truth-inbox">Truth Inbox</h2>
          <div className="record-list">
            {overview.inbox.map((statement) => (
              <button
                type="button"
                className="record-row"
                key={statement.id}
                onClick={() => setSelectedId(statement.id)}
              >
                <span>
                  <strong>{statement.content}</strong>
                  <small>
                    {TYPE_LABELS[statement.statementType]} · {STATUS_LABELS[statement.status]} ·{" "}
                    {sourceLabel(statement)}
                  </small>
                </span>
                <span aria-hidden="true">›</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {falsified.length > 0 && (
        <details className="governance-disclosure">
          <summary>已证伪和已被取代 · {falsified.length}</summary>
          <div className="record-list">
            {falsified.map((statement) => (
              <button
                type="button"
                className="record-row"
                key={statement.id}
                onClick={() => setSelectedId(statement.id)}
              >
                <span>
                  <strong>{statement.content}</strong>
                  <small>{STATUS_LABELS[statement.status]}</small>
                </span>
                <span aria-hidden="true">›</span>
              </button>
            ))}
          </div>
        </details>
      )}

      {showAdd ? (
        <section className="inline-create" aria-labelledby="add-truth">
          <h2 id="add-truth">添加陈述</h2>
          <label className="field">
            <span>陈述类型</span>
            <select
              value={newType}
              onChange={(event) => setNewType(event.target.value as EvidenceStatementType)}
            >
              {Object.entries(TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>内容</span>
            <textarea
              rows={4}
              value={newContent}
              onChange={(event) => setNewContent(event.target.value)}
            />
            <small>新陈述先进入 Inbox 草稿；系统不会自动把它变成事实。</small>
          </label>
          <div className="inline-actions">
            <button type="button" className="secondary-button" onClick={() => setShowAdd(false)}>
              取消
            </button>
            <button type="button" className="primary-button" onClick={() => void createStatement()}>
              保存到 Inbox
            </button>
          </div>
        </section>
      ) : (
        <button
          type="button"
          className="primary-button full-width governance-create"
          onClick={() => setShowAdd(true)}
        >
          添加陈述
        </button>
      )}
      <p className="quiet-link">从历史复盘进入的陈述会保留 review_id，不复制私人原文。</p>
    </main>
  );
}

function StatementDetail({
  database,
  statement,
  allStatements,
  onBack,
  onSaved,
}: {
  database: CEOOSDatabase;
  statement: EvidenceStatement;
  allStatements: EvidenceStatement[];
  onBack: () => void;
  onSaved: () => Promise<void>;
}) {
  const [draft, setDraft] = useState<EvidenceStatement>(statement);
  const [notice, setNotice] = useState("");
  const [saving, setSaving] = useState(false);

  const linkedIds = useMemo(
    () => new Set([...draft.supportsStatementIds, ...draft.evidenceRefs]),
    [draft.evidenceRefs, draft.supportsStatementIds],
  );
  const availableSupports = allStatements.filter(
    (candidate) => candidate.id !== draft.id && candidate.status === "active",
  );

  const toggleSupport = (id: string) => {
    const next = new Set(linkedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    const ids = [...next];
    if (draft.statementType === "inference") {
      setDraft({
        ...draft,
        supportsStatementIds: ids,
        evidenceRefs: ids,
        supportingFactIds: ids.filter(
          (candidateId) =>
            allStatements.find((candidate) => candidate.id === candidateId)?.statementType ===
            "fact",
        ),
      });
    } else if (draft.statementType === "recommendation") {
      setDraft({
        ...draft,
        supportsStatementIds: ids,
        evidenceRefs: ids,
        basedOnStatementIds: ids,
      });
    } else {
      setDraft({ ...draft, supportsStatementIds: ids, evidenceRefs: ids });
    }
  };

  const save = async () => {
    if (!draft.content.trim()) {
      setNotice("陈述内容不能为空。");
      return;
    }
    if (
      draft.statementType === "hypothesis" &&
      (!draft.verificationCondition?.trim() || !draft.falsificationCondition?.trim())
    ) {
      setNotice("假设必须同时填写验证条件和反证条件。");
      return;
    }
    if (draft.statementType === "inference" && draft.supportingFactIds.length === 0) {
      setNotice("推断至少需要一条支持事实。");
      return;
    }
    setSaving(true);
    try {
      const activeDraft: EvidenceStatement = {
        ...draft,
        content: draft.content.trim(),
        status: draft.status === "draft" ? "active" : draft.status,
        userConfirmation: "confirmed",
        truthInboxStatus: "reviewed",
        updatedAt: new Date().toISOString(),
      };
      await updateTruthStatement(database, activeDraft);
      await onSaved();
      setNotice("已保存并重新运行确定性审计。");
    } catch (error) {
      setNotice(safeErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (status: TruthStatementStatus) => {
    setSaving(true);
    try {
      await updateTruthStatement(database, {
        ...draft,
        status,
        truthInboxStatus: "reviewed",
      });
      await onSaved();
      if (status !== "active") onBack();
    } catch (error) {
      setNotice(safeErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="governance-page statement-detail">
      <header className="detail-topbar">
        <button type="button" className="back-button" onClick={onBack}>
          <BackIcon />
          事实审计
        </button>
        <h1>陈述详情</h1>
        <span />
      </header>
      {notice && (
        <div className="inline-notice" role="status">
          {notice}
        </div>
      )}
      <section className="detail-section">
        <div className="detail-meta">
          <span>{TYPE_LABELS[draft.statementType]}</span>
          <span>{STATUS_LABELS[draft.status]}</span>
          <span>{sourceLabel(draft)}</span>
        </div>
        <label className="field">
          <span>内容</span>
          <textarea
            rows={5}
            value={draft.content}
            onChange={(event) => setDraft({ ...draft, content: event.target.value })}
          />
        </label>
        {draft.statementType === "hypothesis" && (
          <>
            <label className="field">
              <span>验证条件</span>
              <textarea
                rows={3}
                value={draft.verificationCondition ?? ""}
                onChange={(event) =>
                  setDraft({ ...draft, verificationCondition: event.target.value })
                }
              />
            </label>
            <label className="field">
              <span>反证条件</span>
              <textarea
                rows={3}
                value={draft.falsificationCondition ?? ""}
                onChange={(event) =>
                  setDraft({ ...draft, falsificationCondition: event.target.value })
                }
              />
            </label>
          </>
        )}
      </section>

      <section className="detail-section">
        <h2>证据与陈述关系</h2>
        {availableSupports.length === 0 ? (
          <p className="section-helper">暂无其他 active 陈述可关联。</p>
        ) : (
          <div className="relationship-list">
            {availableSupports.map((candidate) => (
              <label className="check-row" key={candidate.id}>
                <input
                  type="checkbox"
                  checked={linkedIds.has(candidate.id)}
                  onChange={() => toggleSupport(candidate.id)}
                />
                <span>
                  <strong>{TYPE_LABELS[candidate.statementType]}</strong>
                  {candidate.content}
                </span>
              </label>
            ))}
          </div>
        )}
        <label className="field">
          <span>与哪些陈述矛盾</span>
          <select
            multiple
            value={draft.contradictsStatementIds}
            onChange={(event) =>
              setDraft({
                ...draft,
                contradictsStatementIds: [...event.currentTarget.selectedOptions].map(
                  (option) => option.value,
                ),
              })
            }
          >
            {availableSupports.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.content}
              </option>
            ))}
          </select>
          <small>按 Ctrl / Command 可多选；矛盾关系只提示，不自动改写类型。</small>
        </label>
      </section>

      <section className="detail-section">
        <h2>复核与状态</h2>
        <label className="field">
          <span>最近复核日期</span>
          <input
            type="date"
            value={draft.reviewDueAt?.slice(0, 10) ?? ""}
            onChange={(event) =>
              setDraft({
                ...draft,
                reviewDueAt: `${event.target.value}T23:59:00+08:00`,
              })
            }
          />
        </label>
        <button
          type="button"
          className="primary-button full-width"
          disabled={saving}
          onClick={() => void save()}
        >
          {saving ? "保存中…" : "保存并重新审计"}
        </button>
        <div className="status-action-grid" aria-label="陈述状态">
          <button type="button" onClick={() => void setStatus("verified")}>
            标记已验证
          </button>
          <button type="button" onClick={() => void setStatus("falsified")}>
            标记已证伪
          </button>
          <button type="button" onClick={() => void setStatus("superseded")}>
            标记已被取代
          </button>
          <button type="button" onClick={() => void setStatus("archived")}>
            归档
          </button>
        </div>
      </section>
    </main>
  );
}
